import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Users, DollarSign, Target, Crown, TrendingUp } from "lucide-react";

const initial = { name: "", email: "", audience: "", channels: "", promo_idea: "", tracks: ["brand_license"] };

const COMMISSION_TRACKS = [
  {
    id: "brand_license",
    badge: "NEW",
    badgeStyle: { background: "var(--brass)", color: "var(--bg)" },
    title: "Brand License",
    rate: "20%",
    recurring: "recurring annually",
    price: "$4,500 / yr",
    payout: "≈ $900 / referral / year",
    body: "Refer franchise partners, agencies, broadcast affiliates, or any business that needs to license the WealthFirst marks. You earn 20% every year the licensee renews — compounding for the life of the relationship.",
    icon: Crown,
  },
  {
    id: "franchise",
    title: "Franchise Setup",
    rate: "15%",
    recurring: "one-time",
    price: "$2,400 – $24,000",
    payout: "$360 – $3,600 per setup",
    body: "Refer founders to a WealthFirst franchise tier (Starter, Grow-Rich, Enterprise). Earned on signature of the franchise agreement.",
    icon: TrendingUp,
  },
  {
    id: "funding",
    title: "Funding Portal",
    rate: "10%",
    recurring: "one-time",
    price: "$139 + concierge",
    payout: "$14 + 10% of concierge fees",
    body: "Refer founders to the AI Funding Report or the underwriting concierge. Earned on the customer's first paid product.",
    icon: Target,
  },
  {
    id: "membership",
    title: "Membership",
    rate: "30%",
    recurring: "lifetime recurring",
    price: "$45 – $365 / yr",
    payout: "$13.50 – $109.50 / yr / member",
    body: "Refer subscribers to the editorial archive. Earn 30% of every membership renewal for the life of the member.",
    icon: Users,
  },
];

const TRACK_LABELS = {
  brand_license: "Brand License ($4,500/yr → 20%)",
  franchise: "Franchise Setup ($2,400–$24,000 → 15%)",
  funding: "Funding Portal ($139+ → 10%)",
  membership: "Membership ($45–$365 → 30% lifetime)",
};

export default function Affiliate() {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTrack = (id) =>
    setForm((f) => ({
      ...f,
      tracks: f.tracks.includes(id) ? f.tracks.filter((t) => t !== id) : [...f.tracks, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.tracks.length === 0) {
      toast.error("Pick at least one commission track to promote.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/affiliate", form);
      setDone(true);
      toast.success("Affiliate application received.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="affiliate-done" className="min-h-[80vh] flex items-center justify-center px-6 pt-24">
        <div className="max-w-xl text-center reveal">
          <CheckCircle2 size={48} style={{ color: "var(--brass)", margin: "0 auto 1.5rem" }} />
          <p className="eyebrow mb-4">Application Received</p>
          <h1 className="font-display text-5xl md:text-6xl mb-6 leading-[0.95]">
            Welcome, <em style={{ color: "var(--brass)" }}>partner</em>.
          </h1>
          <p className="text-lg" style={{ color: "var(--ink-muted)" }}>
            We review every affiliate application personally. You will hear back within three business days
            with onboarding instructions, your unique referral code, and promotional materials —
            including the new $4,500 brand-license referral toolkit.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="affiliate-page" className="max-w-[1180px] mx-auto px-6 lg:px-10 pt-32 pb-24">
      {/* Hero */}
      <div className="mb-16 reveal">
        <p className="eyebrow mb-4">Affiliate Program · 2026 Edition</p>
        <h1 className="font-display text-6xl md:text-7xl mb-6 leading-[0.95]">
          Refer. Earn. <em style={{ color: "var(--brass)" }}>Compound</em>.
        </h1>
        <p className="text-lg max-w-3xl leading-relaxed" style={{ color: "var(--ink-muted)" }}>
          Become a Wealth First Inc. affiliate across four earnings tracks — including the new{" "}
          <span style={{ color: "var(--brass)" }}>$4,500 annual Brand License</span> referral track that pays{" "}
          <span style={{ color: "var(--brass)" }}>$900 per licensee, every year</span> they renew.
        </p>
      </div>

      <hr className="hairline mb-12" />

      {/* Four-track value props */}
      <div className="mb-12">
        <p className="eyebrow mb-4">Four Earnings Tracks</p>
        <h2 className="font-display text-4xl md:text-5xl mb-10 leading-tight">
          Compound across the <em style={{ color: "var(--brass)" }}>WealthFirst</em> stack.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {COMMISSION_TRACKS.map((t) => (
            <div
              key={t.id}
              className="p-8 relative"
              style={{
                border: t.id === "brand_license" ? "1px solid var(--brass)" : "1px solid var(--hairline)",
                background: "var(--surface)",
                overflow: "hidden",
              }}
              data-testid={`track-card-${t.id}`}
            >
              {t.id === "brand_license" && (
                <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at top right, rgba(194, 163, 115, 0.08), transparent 50%)", pointerEvents: "none" }} />
              )}
              <div style={{ position: "relative", zIndex: 1 }}>
                <div className="flex items-start justify-between gap-4 mb-5">
                  <t.icon size={26} style={{ color: "var(--brass)" }} />
                  {t.badge && (
                    <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1" style={t.badgeStyle}>
                      {t.badge}
                    </span>
                  )}
                </div>
                <h4 className="font-display text-3xl mb-2 leading-tight">{t.title}</h4>
                <div className="flex items-baseline gap-3 mb-5">
                  <span className="font-display text-5xl" style={{ color: "var(--brass)" }}>{t.rate}</span>
                  <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--ink-muted)" }}>
                    {t.recurring}
                  </span>
                </div>
                <div className="text-xs uppercase tracking-[0.18em] mb-1" style={{ color: "var(--ink-faint)" }}>Customer price</div>
                <div className="font-display text-xl mb-4">{t.price}</div>
                <div className="text-xs uppercase tracking-[0.18em] mb-1" style={{ color: "var(--ink-faint)" }}>Your payout</div>
                <div className="font-display text-xl mb-5" style={{ color: "var(--brass)" }}>{t.payout}</div>
                <p className="text-sm leading-relaxed" style={{ color: "var(--ink-muted)" }}>{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <hr className="hairline mb-12" />

      {/* Earnings calculator for brand-license referrer */}
      <div className="mb-16 p-10" style={{ border: "1px solid var(--brass)", background: "var(--surface)" }} data-testid="affiliate-calculator">
        <p className="eyebrow mb-4" style={{ color: "var(--brass)" }}>Brand-License Math</p>
        <h2 className="font-display text-4xl md:text-5xl leading-tight mb-6">
          One <em style={{ color: "var(--brass)" }}>franchise affiliate</em>. <br />
          Five referrals. <em style={{ color: "var(--brass)" }}>$4,500</em> a year. <em style={{ color: "var(--brass)" }}>Renewable</em>.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8 text-center">
          <CalcCell label="Referrals" value="5" />
          <CalcCell label="Year 1 commission" value="$4,500" />
          <CalcCell label="Year 2 (renewals)" value="$4,500" highlight />
          <CalcCell label="5-year recurring" value="$22,500" highlight />
        </div>
        <p className="text-xs mt-6 text-center" style={{ color: "var(--ink-muted)" }}>
          Assumes 100% annual renewal · Actual payouts net of refunds, ACH/wire fees.
        </p>
      </div>

      <hr className="hairline mb-12" />

      {/* Toolkit */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
        <ValueProp icon={DollarSign} title="Monthly Payouts" body="ACH (US) or international wire. Net-15 from each customer payment." />
        <ValueProp icon={Users} title="Unique Referral Code" body="One trackable URL per affiliate. UTM-tagged automatically across the platform." />
        <ValueProp icon={Target} title="Full Toolkit" body="Branded creatives, copy templates, the official /logo.html brand-kit reference, and 1:1 onboarding call." />
      </div>

      <hr className="hairline mb-12" />

      {/* Apply */}
      <form onSubmit={submit} className="space-y-7 max-w-3xl" data-testid="affiliate-form">
        <div>
          <p className="eyebrow mb-2">Apply</p>
          <h2 className="font-display text-4xl mb-6">Tell us who you reach.</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="eyebrow block mb-2">Your Name *</label>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="input-line" data-testid="aff-name" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Email *</label>
            <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-line" data-testid="aff-email" />
          </div>
        </div>

        <div>
          <label className="eyebrow block mb-2">Describe your audience *</label>
          <input
            required
            value={form.audience}
            onChange={(e) => set("audience", e.target.value)}
            placeholder="e.g. franchise consultants, branding agencies, broadcast affiliates, UHNW family-office advisors"
            className="input-line"
            data-testid="aff-audience"
          />
        </div>

        <div>
          <label className="eyebrow block mb-2">Your channels *</label>
          <input
            required
            value={form.channels}
            onChange={(e) => set("channels", e.target.value)}
            placeholder="e.g. LinkedIn (28k), Newsletter (8k), in-person franchise expos"
            className="input-line"
            data-testid="aff-channels"
          />
        </div>

        <div data-testid="aff-tracks">
          <label className="eyebrow block mb-3">Which tracks will you promote? *</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(TRACK_LABELS).map(([id, label]) => (
              <label
                key={id}
                className="flex items-center gap-3 p-4 cursor-pointer transition-colors hover:bg-[var(--surface)]"
                style={{
                  border: "1px solid",
                  borderColor: form.tracks.includes(id) ? "var(--brass)" : "var(--hairline-strong)",
                  color: form.tracks.includes(id) ? "var(--ink)" : "var(--ink-muted)",
                }}
                data-testid={`aff-track-${id}`}
              >
                <input
                  type="checkbox"
                  checked={form.tracks.includes(id)}
                  onChange={() => toggleTrack(id)}
                  className="w-4 h-4 accent-[var(--brass)]"
                />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="eyebrow block mb-2">How would you promote WealthFirst?</label>
          <textarea
            rows={5}
            value={form.promo_idea}
            onChange={(e) => set("promo_idea", e.target.value)}
            placeholder="A short sketch of your promo plan. Include any existing relationships with franchise consultants, agencies, or broadcast partners."
            className="w-full bg-transparent p-3 text-base outline-none"
            style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink)" }}
            data-testid="aff-promo"
          />
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={busy} className="btn-brass" data-testid="aff-submit">
            {busy ? "Submitting…" : "Apply to Affiliate Program"}
          </button>
        </div>
      </form>

      <hr className="hairline my-16" />

      {/* Brand-kit reference for affiliates */}
      <div className="text-center" data-testid="affiliate-brand-kit-link">
        <p className="eyebrow mb-4">For Approved Affiliates</p>
        <p className="text-base mb-6" style={{ color: "var(--ink-muted)" }}>
          Need brand assets to promote? The full WealthFirst brand-kit reference is publicly viewable —
          downloadable assets are unlocked after approval.
        </p>
        <a
          href="/logo.html"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-3 px-6 py-3 text-xs uppercase tracking-[0.18em]"
          style={{ border: "1px solid var(--brass)", color: "var(--brass)" }}
        >
          Open /logo.html Brand Kit →
        </a>
      </div>
    </div>
  );
}

function ValueProp({ icon: Icon, title, body }) {
  return (
    <div className="p-8" style={{ border: "1px solid var(--hairline)" }}>
      <Icon size={24} style={{ color: "var(--brass)" }} className="mb-4" />
      <h4 className="font-display text-2xl mb-3">{title}</h4>
      <p className="text-sm leading-relaxed" style={{ color: "var(--ink-muted)" }}>{body}</p>
    </div>
  );
}

function CalcCell({ label, value, highlight }) {
  return (
    <div className="p-5" style={{ border: "1px solid var(--hairline)" }}>
      <p className="eyebrow mb-2" style={{ color: "var(--ink-faint)" }}>{label}</p>
      <p className="font-display text-3xl" style={{ color: highlight ? "var(--brass)" : "var(--ink)" }}>{value}</p>
    </div>
  );
}
